import { useFormContext, useFormState } from 'react-hook-form';
import { Box, CircularProgress } from '@mui/material';
import PropTypes from 'prop-types';
import Button from '../../theme/components/Button.jsx';

/**
 * A submit button that is aware of the form's submission state.
 * It automatically shows a loading spinner and disables itself during submission
 * or if the form is not dirty or valid.
 * @param {object} control - The control object from react-hook-form. Can be omitted if used inside a FormProvider.
 * @param {React.ReactNode} children - The button text.
 * @param {boolean} disabled - An additional flag to disable the button.
 * @param {boolean} requireDirty - If true, button requires a dirty form before enabling.
 * @param {'primary'|'secondary'|'tertiary'|'neutral'} variant - Design system button variant (default: primary).
 * @param {object} otherProps - Other props to pass to the Button.
 * @returns {JSX.Element}
 */
const SubmitButton = ({ control, children, disabled, requireDirty = true, variant = 'primary', ...otherProps }) => {
  const context = useFormContext();
  const formControl = control || context?.control;

  if (!formControl) {
    throw new Error('SubmitButton must be used within a FormProvider or have a control prop.');
  }

  const { isSubmitting, isDirty, isValid } = useFormState({ control: formControl });
  const isDisabled = (requireDirty && !isDirty) || !isValid || isSubmitting || disabled;

  return (
    <Box sx={{ position: 'relative', alignSelf: 'flex-start' }}>
      <Button
        type="submit"
        variant={variant}
        disabled={isDisabled}
        {...otherProps}
      >
        {children}
      </Button>
      {isSubmitting && (
        <CircularProgress
          size={24}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            marginTop: '-12px',
            marginLeft: '-12px',
          }}
        />
      )}
    </Box>
  );
};

SubmitButton.propTypes = {
  control: PropTypes.object,
  children: PropTypes.node.isRequired,
  disabled: PropTypes.bool,
  requireDirty: PropTypes.bool,
  variant: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'neutral']),
};

export default SubmitButton;
